import type {
    OverviewAgeBucketCounts,
    OverviewCategory
} from '../../../services/dmMonitoringOverview';
import { OverviewCard } from '../../billing/overview/OverviewCard';
import { shareOf } from './overviewFormat';
import { AGE_BUCKET_LABELS, CATEGORY_LEGEND, OVERDUE_AGE_BUCKET } from './overviewLegend';
import { EmptyNote, SwatchLegend } from './StackedBar';

const CATEGORIES: readonly OverviewCategory[] = ['notification', 'message'];
const COUNT_FIELD = { notification: 'notifications', message: 'messages' } as const;

const LEGEND_ITEMS = CATEGORIES.map((category) => ({
    key: category,
    ...CATEGORY_LEGEND[category]
}));

interface ColumnBarProps {
    count: number;
    max: number;
    swatch: string;
    label: string;
}

function ColumnBar({ count, max, swatch, label }: ColumnBarProps) {
    return (
        <span
            title={`${label}: ${count}`}
            className={`relative w-5 rounded-t sm:w-7 transition-[height] duration-500 ease-out ${swatch}`}
            style={{ height: `${shareOf(count, max)}%` }}
        >
            <span className="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 text-[11px] font-semibold tabular-nums text-gray-700">
                {count}
            </span>
        </span>
    );
}

function BucketColumn({ bucket, max }: { bucket: OverviewAgeBucketCounts; max: number }) {
    return (
        <div className="flex h-full items-end justify-center gap-1.5">
            {CATEGORIES.map((category) => (
                <ColumnBar
                    key={category}
                    count={bucket[COUNT_FIELD[category]]}
                    max={max}
                    swatch={CATEGORY_LEGEND[category].swatch}
                    label={`${AGE_BUCKET_LABELS[bucket.bucket]}, ${CATEGORY_LEGEND[category].label.toLowerCase()}`}
                />
            ))}
        </div>
    );
}

export function BacklogAgeCard({ buckets }: { buckets: readonly OverviewAgeBucketCounts[] }) {
    const max = Math.max(0, ...buckets.flatMap((b) => [b.notifications, b.messages]));
    return (
        <OverviewCard
            title="Backlog age"
            info="Time since DM raised each open item"
            action={<SwatchLegend items={LEGEND_ITEMS} />}
        >
            {max === 0 ? (
                <EmptyNote>No open items.</EmptyNote>
            ) : (
                <div className="flex flex-col gap-2">
                    {/* Top padding leaves room for the count label floating above the tallest bar. */}
                    <div className="grid h-[150px] grid-cols-4 gap-4 border-b border-gray-200 px-2 pt-5">
                        {buckets.map((bucket) => (
                            <BucketColumn key={bucket.bucket} bucket={bucket} max={max} />
                        ))}
                    </div>
                    <div className="grid grid-cols-4 gap-4 px-2">
                        {buckets.map((bucket) => (
                            <span
                                key={bucket.bucket}
                                className={`text-center text-xs ${
                                    bucket.bucket === OVERDUE_AGE_BUCKET
                                        ? 'font-semibold text-amber-700'
                                        : 'text-gray-500'
                                }`}
                            >
                                {AGE_BUCKET_LABELS[bucket.bucket]}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </OverviewCard>
    );
}
